import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import InputForm from './components/InputForm'
import FurnitureList from './components/FurnitureList'
import ResultPanel from './components/ResultPanel'
import Room3D from './components/Room3D'
import CameraUpdater from './components/CameraUpdater'
import { checkAllCollisions, findEmptyPosition } from './utils/collisionDetection'
import ResultModal from './components/ResultModal'
import RoomSettingsModal from './components/RoomSettingsModal'
import { useHistory } from './hooks/useHistory'
import { CATEGORIES } from './constants/furniture'

// モバイル判定のカスタムフック
function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint)

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < breakpoint)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [breakpoint])

    return isMobile
}

function App() {
    const [room, setRoom] = useState({ width: 350, depth: 260, height: 240 }) // 6畳デフォルト
    // Undo/Redo対応の家具リスト状態管理
    const { state: furnitureList, setState: setFurnitureList, undo, redo, canUndo, canRedo } = useHistory([])
    const [selectedId, setSelectedId] = useState(null)
    const [nextId, setNextId] = useState(1)
    const [cameraPosition, setCameraPosition] = useState([6, 6, 6])
    const [cameraTarget, setCameraTarget] = useState([0, 0, 0]) // カメラの注視点
    const [ignoredCollisions, setIgnoredCollisions] = useState(new Set())
    const [isResultModalOpen, setIsResultModalOpen] = useState(false)
    const [isRoomSettingsModalOpen, setIsRoomSettingsModalOpen] = useState(true) // 初回は表示
    const [showGuides, setShowGuides] = useState(false) // ガイド表示状態
    const [isFirstTimeSetup, setIsFirstTimeSetup] = useState(true) // 初回設定フラグ
    const [activeTab, setActiveTab] = useState('add') // モバイル用タブ状態
    const [isPanelMinimized, setIsPanelMinimized] = useState(false) // モバイル下部パネル最小化
    const [isUiHidden, setIsUiHidden] = useState(false) // UI表示非表示
    const [isSnapEnabled, setIsSnapEnabled] = useState(true) // スナップ機能ON/OFF
    const [openContextMenuId, setOpenContextMenuId] = useState(null) // コンテキストメニューを開く家具ID

    // タブごとのデフォルトパネル高さ（%）
    const TAB_DEFAULT_HEIGHTS = {
        add: 55,   // 家具追加タブ
        list: 65   // 配置済み家具タブ
    }

    const [panelHeight, setPanelHeight] = useState(TAB_DEFAULT_HEIGHTS['add']) // パネル高さ（%）
    const [mobileSelectedCategory, setMobileSelectedCategory] = useState('desk') // モバイル用選択カテゴリ
    const [isDragging, setIsDragging] = useState(false)
    const dragStartRef = useRef({ y: 0, height: 0 })
    const controlsRef = useRef()
    const canvasContainerRef = useRef()
    const sceneRef = useRef()
    const furnitureGroupRef = useRef()
    const isMobile = useIsMobile()
    const dragHandleRef = useRef()

    // Canvas用メモ化値（条件付きレンダリングでのフック順序エラー防止のためトップレベルで宣言）
    const canvasCamera = useMemo(() => ({ position: cameraPosition, fov: 50 }), [cameraPosition])
    const canvasGl = useMemo(() => ({ preserveDrawingBuffer: true }), [])
    const canvasDpr = useMemo(() => [1, 2], [])

    // キーボードショートカット (Ctrl+Z: Undo, Ctrl+Y / Ctrl+Shift+Z: Redo, Delete: 選択家具削除)
    useEffect(() => {
        const handleKeyDown = (e) => {
            // 入力フォームにフォーカスがある場合は無視
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault()
                undo()
            } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
                e.preventDefault()
                redo()
            } else if (e.key === 'Delete' && selectedId !== null) {
                e.preventDefault()
                handleDeleteFurniture(selectedId)
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [undo, redo, selectedId])


    // ドラッグ開始
    const handleDragStart = (e) => {
        // タッチ操作の場合、スクロール等のデフォルト動作を防ぐためにCSSでtouch-action: noneを設定済み
        // ReactのPassive Event Listener警告を防ぐため、ここではpreventDefaultを呼ばない

        const clientY = e.touches ? e.touches[0].clientY : e.clientY
        dragStartRef.current = { y: clientY, height: panelHeight }
        setIsDragging(true)
    }

    // ドラッグ中
    useEffect(() => {
        if (!isDragging) return

        const handleMove = (e) => {
            e.preventDefault() // スクロールを防止
            const clientY = e.touches ? e.touches[0].clientY : e.clientY
            const deltaY = dragStartRef.current.y - clientY
            const windowHeight = window.innerHeight
            const deltaPercent = (deltaY / windowHeight) * 100
            // 最小高さをピクセル値（100px = タイトル+タブ+罫線）で制限
            const minHeightPercent = (100 / windowHeight) * 100
            const newHeight = Math.max(minHeightPercent, Math.min(80, dragStartRef.current.height + deltaPercent))
            setPanelHeight(newHeight)
        }

        const handleEnd = () => {
            setIsDragging(false)
        }

        document.addEventListener('mousemove', handleMove)
        document.addEventListener('mouseup', handleEnd)
        document.addEventListener('touchmove', handleMove, { passive: false })
        document.addEventListener('touchend', handleEnd)

        return () => {
            document.removeEventListener('mousemove', handleMove)
            document.removeEventListener('mouseup', handleEnd)
            document.removeEventListener('touchmove', handleMove)
            document.removeEventListener('touchend', handleEnd)
        }
    }, [isDragging])

    // 家具を追加
    const handleAddFurniture = (dimensions) => {
        if (dimensions.action === 'add') {
            const categoryId = dimensions.categoryId || 'desk'
            const category = CATEGORIES.find(c => c.id === categoryId)

            const newSize = {
                width: dimensions.width,
                height: dimensions.height,
                depth: dimensions.depth,
            }

            // Smart Spawn: 空きスペースを探索
            const spawnPosition = findEmptyPosition(newSize, furnitureList, room, 0)

            const newFurniture = {
                id: `furniture-${nextId}`,
                name: `${category.name} ${nextId}`,
                icon: category.icon,
                category: categoryId,     // カテゴリID（desk, chair等）
                subtype: 'default',       // デフォルトは四角
                size: newSize,
                position: { x: spawnPosition.x, y: 0, z: spawnPosition.z },
                rotation: 0,              // Y軸の回転角度（度数法）
            }
            setFurnitureList([...furnitureList, newFurniture])
            setNextId(nextId + 1)
            setSelectedId(newFurniture.id)
            setOpenContextMenuId(newFurniture.id) // コンテキストメニューを自動表示

            // 空きスペースがなかった場合の警告（コンソールに出力）
            if (!spawnPosition.hasSpace) {
                console.warn('⚠️ 空きスペースが見つからなかったため、原点付近に配置しました。他の家具と重なっている可能性があります。')
            }
        }
    }

    // 家具を複製
    const handleDuplicateFurniture = (id) => {
        const original = furnitureList.find(f => f.id === id)
        if (!original) return

        // 新しい位置を探索
        const spawnPosition = findEmptyPosition(original.size, furnitureList, room, original.rotation || 0)

        const newFurniture = {
            ...original,
            id: `furniture-${nextId}`,
            name: `${original.name.replace(/\s\d+$/, '')} ${nextId}`,
            position: { x: spawnPosition.x, y: 0, z: spawnPosition.z },
        }
        setFurnitureList([...furnitureList, newFurniture])
        setNextId(nextId + 1)
        setSelectedId(newFurniture.id)
        setOpenContextMenuId(newFurniture.id) // 複製後コンテキストメニューを開く
    }

    // 家具を削除
    const handleDeleteFurniture = (id) => {
        setFurnitureList(furnitureList.filter(f => f.id !== id))
        if (selectedId === id) {
            setSelectedId(null)
        }
    }

    // 家具のサイズを更新
    const handleUpdateFurnitureSize = (id, dimension, value, overwrite = false) => {
        setFurnitureList(furnitureList.map(f => {
            if (f.id === id) {
                return {
                    ...f,
                    size: {
                        ...f.size,
                        [dimension]: value
                    }
                }
            }
            return f
        }), overwrite)
    }

    // 家具の位置を更新
    const handleUpdateFurniturePosition = (id, axis, value, overwrite = false) => {
        setFurnitureList(furnitureList.map(f => {
            if (f.id === id) {
                return {
                    ...f,
                    position: {
                        ...f.position,
                        [axis]: value
                    }
                }
            }
            return f
        }), overwrite)
    }

    // 家具の位置を一括更新（ドラッグ用）
    const handleUpdateFurniturePositionBatch = (id, newX, newZ, overwrite = false) => {
        setFurnitureList(prev => prev.map(f => {
            if (f.id === id) {
                return {
                    ...f,
                    position: {
                        ...f.position,
                        x: newX,
                        z: newZ
                    }
                }
            }
            return f
        }), overwrite)
    }

    // 家具のサイズと位置を同時に更新（片側固定リサイズ用）
    const handleUpdateFurnitureSizeAndPosition = (id, sizeDim, sizeValue, newX, newZ, overwrite = false) => {
        setFurnitureList(prev => prev.map(f => {
            if (f.id === id) {
                return {
                    ...f,
                    size: {
                        ...f.size,
                        [sizeDim]: sizeValue
                    },
                    position: {
                        ...f.position,
                        x: newX,
                        z: newZ
                    }
                }
            }
            return f
        }), overwrite)
    }

    // 家具のサブタイプを更新（サイズも更新）
    const handleUpdateFurnitureSubtype = (id, subtype) => {
        setFurnitureList(furnitureList.map(f => {
            if (f.id === id) {
                // カテゴリを取得してサブタイプのサイズを適用
                const category = CATEGORIES.find(c => c.id === f.category)
                const newSize = category?.subtypeSizes?.[subtype] || category?.defaultSize || f.size
                return {
                    ...f,
                    subtype: subtype,
                    size: { ...newSize }
                }
            }
            return f
        }))
    }

    // 家具を選択（選択解除は行わない）
    const handleSelectFurniture = (id) => {
        // 3Dビューからの選択時は常に選択状態にする（再クリックで解除しない）
        setSelectedId(id)
    }

    // 家具の回転を更新
    const handleUpdateFurnitureRotation = (id, rotation, overwrite = false) => {
        setFurnitureList(furnitureList.map(f => {
            if (f.id === id) {
                return {
                    ...f,
                    rotation: rotation
                }
            }
            return f
        }), overwrite)
    }

    // カメラ視点を変更
    const handleViewChange = (position) => {
        setCameraPosition(position)
        if (controlsRef.current) {
            controlsRef.current.target.set(0, 0, 0)
        }
    }

    // 干渉を無視
    const handleIgnoreCollision = (id1, id2) => {
        const key = [id1, id2].sort().join(':')
        setIgnoredCollisions(new Set([...ignoredCollisions, key]))
    }

    // 干渉の無視を解除
    const handleUnignoreCollision = (id1, id2) => {
        const key = [id1, id2].sort().join(':')
        const newSet = new Set(ignoredCollisions)
        newSet.delete(key)
        setIgnoredCollisions(newSet)
    }

    // 衝突判定を実行
    const collisionResult = checkAllCollisions(furnitureList, room)

    // モバイルレイアウト
    if (isMobile) {
        return (
            <div className="w-full h-full flex flex-col relative overflow-hidden">
                {/* 上部バー - 視点切替、判定ステータス、完成ボタン */}
                <div className="flex-shrink-0 flex items-center justify-between px-2 sm:px-3 py-2 glass-effect border-b border-white/10 overflow-x-auto gap-2 relative z-50">
                    <div className="flex gap-1">
                        {[
                            { id: 'default', icon: '🔄', position: [8, 8, 8] },
                            { id: 'top', icon: '⬆️', position: [0, 15, 0] },
                            { id: 'side', icon: '↔️', position: [15, 5, 0] },
                        ].map((view) => (
                            <button
                                key={view.id}
                                onClick={() => handleViewChange(view.position)}
                                className="p-2 bg-white/10 hover:bg-primary-500/30 text-white text-sm rounded-lg transition-all"
                            >
                                {view.icon}
                            </button>
                        ))}
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowGuides(!showGuides)}
                            className={`p-2 text-white text-sm rounded-lg transition-all ${showGuides ? 'bg-yellow-500/40' : 'bg-white/10'}`}
                            title={showGuides ? 'ガイドOFF' : 'ガイドON'}
                        >
                            📏
                        </button>
                        {/* スナップトグルボタン */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation()
                                setIsSnapEnabled(!isSnapEnabled)
                            }}
                            className={`p-2 text-white text-sm rounded-lg transition-all ${isSnapEnabled ? 'bg-primary-500/70 border border-primary-400' : 'bg-white/10 hover:bg-white/20'}`}
                            title={isSnapEnabled ? 'スナップOFF' : 'スナップON'}
                        >
                            🧲
                        </button>
                        <button
                            onClick={() => {
                                setIsFirstTimeSetup(false)
                                setIsRoomSettingsModalOpen(true)
                            }}
                            className="p-2 bg-white/10 hover:bg-primary-500/30 text-white text-sm rounded-lg transition-all"
                            title="部屋設定"
                        >
                            ⚙️
                        </button>
                        <button
                            onClick={() => setIsResultModalOpen(true)}
                            className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl shadow-lg flex items-center gap-1 text-sm"
                        >
                            <span>✨</span>
                            <span>完成！</span>
                        </button>
                    </div>
                </div>

                {/* 3Dビュー */}
                <div ref={canvasContainerRef} className="flex-1 min-h-0 relative">
                    {/* ガイドオーバーレイ */}
                    {showGuides && (
                        <div className="absolute inset-0 pointer-events-none z-10">
                            {/* 外枠 */}
                            <div className="absolute inset-4 border-2 border-yellow-400/30 rounded-lg" />
                            {/* 中央十字線 */}
                            <div className="absolute top-1/2 left-0 w-full h-px bg-yellow-400/30" />
                            <div className="absolute left-1/2 top-0 w-px h-full bg-yellow-400/30" />
                            {/* 中央ドット */}
                            <div className="absolute top-1/2 left-1/2 w-1.5 h-1.5 bg-yellow-400 rounded-full transform -translate-x-1/2 -translate-y-1/2" />
                        </div>
                    )}
                    <Canvas
                        camera={canvasCamera}
                        className="w-full h-full"
                        gl={canvasGl}
                        dpr={canvasDpr}
                    >
                        <CameraUpdater position={cameraPosition} target={cameraTarget} controlsRef={controlsRef} />
                        <Room3D
                            room={room}
                            furnitureList={furnitureList}
                            collisionResult={collisionResult}
                            selectedId={selectedId}
                            ignoredCollisions={ignoredCollisions}
                            sceneRef={sceneRef}
                            furnitureGroupRef={furnitureGroupRef}
                            onSelect={handleSelectFurniture}
                            isMobile={isMobile}
                            controlsRef={controlsRef}
                            onUpdatePosition={handleUpdateFurniturePosition}
                            onUpdatePositionBatch={handleUpdateFurniturePositionBatch}
                            onUpdateSize={handleUpdateFurnitureSize}
                            onUpdateRotation={handleUpdateFurnitureRotation}
                            onUpdateSubtype={handleUpdateFurnitureSubtype}
                            onDelete={handleDeleteFurniture}
                            onIgnoreCollision={handleIgnoreCollision}
                            onUnignoreCollision={handleUnignoreCollision}
                            isSnapEnabled={isSnapEnabled}
                        />
                        <OrbitControls
                            ref={controlsRef}
                            enableDamping
                            dampingFactor={0.05}
                            minDistance={5}
                            maxDistance={30}
                            onEnd={(e) => {
                                if (e?.target?.object) {
                                    const cam = e.target.object
                                    const ctrl = e.target
                                    setCameraPosition([cam.position.x, cam.position.y, cam.position.z])
                                    if (ctrl.target) {
                                        setCameraTarget([ctrl.target.x, ctrl.target.y, ctrl.target.z])
                                    }
                                }
                            }}
                        />
                    </Canvas>
                </div>

                {/* 操作ヒント */}
                <div className="flex-shrink-0 text-center py-1 bg-black/30">
                    <p className="text-xs text-gray-400">
                        タッチ操作で回転 | ピンチでズーム
                    </p>
                </div>

                {/* 下部パネル - タブ切替式 */}
                <div
                    className={`flex-shrink-0 flex flex-col glass-effect border-t border-white/10 ${isDragging ? '' : 'transition-all duration-300'}`}
                    style={{ height: isPanelMinimized ? 'auto' : `${panelHeight}%` }}
                >
                    {/* ドラッグハンドル */}
                    <div
                        className="flex-shrink-0 flex justify-center py-3 cursor-ns-resize touch-none select-none"
                        style={{ touchAction: 'none' }}
                        onMouseDown={handleDragStart}
                        onTouchStart={handleDragStart}
                    >
                        <div className="w-12 h-1.5 bg-gray-400 rounded-full" />
                    </div>

                    {/* タイトルとタブ */}
                    <div className="flex-shrink-0 px-3 pb-2 border-b border-white/10">
                        <div className="flex justify-between items-center mb-2">
                            <h1 className="text-sm font-bold text-white flex items-center gap-1.5">
                                <img src="/favicon.png" alt="" className="h-5 w-5" />
                                Furniture Checker
                            </h1>
                            <button
                                onClick={() => {
                                    setIsPanelMinimized(!isPanelMinimized)
                                    if (isPanelMinimized) setPanelHeight(55) // 展開時は55%に戻す
                                }}
                                className="text-gray-400 hover:text-white px-2 py-1 rounded-lg bg-white/5 text-xs"
                            >
                                {isPanelMinimized ? '▲ 展開' : '▼ 最小化'}
                            </button>
                        </div>
                        {!isPanelMinimized && (
                            <div className="flex gap-1">
                                {[
                                    { id: 'add', label: '家具追加', icon: '➕' },
                                    { id: 'list', label: '配置済み家具', icon: '📋' },
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => {
                                            setActiveTab(tab.id)
                                            setPanelHeight(TAB_DEFAULT_HEIGHTS[tab.id])
                                        }}
                                        className={`flex-1 py-2 px-2 text-xs font-medium rounded-lg transition-all ${activeTab === tab.id
                                            ? 'bg-primary-500 text-white'
                                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                                            }`}
                                    >
                                        <span className="mr-1">{tab.icon}</span>
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* タブコンテンツ */}
                    {!isPanelMinimized && (
                        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
                            <div className="p-3">
                                {activeTab === 'add' && (
                                    <InputForm onSizeChange={handleAddFurniture} hideHeader />
                                )}
                                {activeTab === 'list' && (
                                    <FurnitureList
                                        furnitureList={furnitureList}
                                        collisionResult={collisionResult}
                                        onDelete={handleDeleteFurniture}
                                        onSelect={handleSelectFurniture}
                                        selectedId={selectedId}
                                        onUpdateSize={handleUpdateFurnitureSize}
                                        onUpdatePosition={handleUpdateFurniturePosition}
                                        onUpdateSubtype={handleUpdateFurnitureSubtype}
                                        onUpdateRotation={handleUpdateFurnitureRotation}
                                        room={room}
                                        ignoredCollisions={ignoredCollisions}
                                        onIgnoreCollision={handleIgnoreCollision}
                                        onUnignoreCollision={handleUnignoreCollision}
                                        hideHeader
                                        onDuplicate={handleDuplicateFurniture}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                </div>

                {/* リザルトモーダル */}
                <ResultModal
                    isOpen={isResultModalOpen}
                    onClose={() => setIsResultModalOpen(false)}
                    furnitureList={furnitureList}
                    canvasRef={canvasContainerRef}
                    sceneRef={sceneRef}
                    furnitureGroupRef={furnitureGroupRef}
                />

                {/* 部屋設定モーダル */}
                <RoomSettingsModal
                    isOpen={isRoomSettingsModalOpen}
                    onClose={() => {
                        setIsRoomSettingsModalOpen(false)
                        setIsFirstTimeSetup(false)
                    }}
                    room={room}
                    onRoomChange={setRoom}
                    isFirstTime={isFirstTimeSetup}
                />
            </div>
        )
    }

    // デスクトップレイアウト（従来のまま）
    return (
        <div className="w-full h-full relative">
            {/* 上部バー - ボタン群 */}
            {!isUiHidden && (
                <div className="absolute top-6 left-1/2 -translate-x-1/2 z-10 flex gap-10">
                    {/* Undo/Redoボタングループ */}
                    <div className="flex gap-2">
                        <button
                            onClick={undo}
                            disabled={!canUndo}
                            className={`px-3 py-2 glass-effect text-white text-sm rounded-lg transition-all flex items-center gap-1 ${!canUndo ? 'opacity-40 cursor-not-allowed' : 'hover:bg-primary-500/30'}`}
                            title="元に戻す (Ctrl+Z)"
                        >
                            <span>↩️</span>
                            <span className="text-xs">戻る</span>
                        </button>
                        <button
                            onClick={redo}
                            disabled={!canRedo}
                            className={`px-3 py-2 glass-effect text-white text-sm rounded-lg transition-all flex items-center gap-1 ${!canRedo ? 'opacity-40 cursor-not-allowed' : 'hover:bg-primary-500/30'}`}
                            title="やり直す (Ctrl+Y)"
                        >
                            <span>↪️</span>
                            <span className="text-xs">進む</span>
                        </button>
                    </div>

                    {/* 視点切替ボタングループ */}
                    <div className="flex gap-2">
                        {[
                            { id: 'default', name: '初期視点', icon: '🔄', position: [8, 8, 8] },
                            { id: 'top', name: '上視点', icon: '⬆️', position: [0, 15, 0] },
                            { id: 'side', name: '側面視点', icon: '↔️', position: [15, 5, 0] },
                        ].map((view) => (
                            <button
                                key={view.id}
                                onClick={() => handleViewChange(view.position)}
                                className="px-4 py-2 glass-effect hover:bg-primary-500/30 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                                title={view.name}
                            >
                                <span>{view.icon}</span>
                                <span className="text-xs">{view.name}</span>
                            </button>
                        ))}
                    </div>

                    {/* スナップ・UI非表示ボタングループ */}
                    <div className="flex gap-2">
                        {/* ガイドトグルボタン */}
                        <button
                            onClick={() => setShowGuides(!showGuides)}
                            className={`px-4 py-2 glass-effect text-white text-sm rounded-lg transition-all flex items-center gap-2 ${showGuides ? 'bg-yellow-500/40' : 'hover:bg-yellow-500/30'}`}
                            title={showGuides ? 'ガイドOFF' : 'ガイドON'}
                        >
                            <span>📏</span>
                            <span className="text-xs">{showGuides ? 'ガイドON' : 'ガイドOFF'}</span>
                        </button>
                        {/* スナップトグルボタン */}
                        <button
                            onClick={() => setIsSnapEnabled(!isSnapEnabled)}
                            className={`px-4 py-2 glass-effect text-white text-sm rounded-lg transition-all flex items-center gap-2 ${isSnapEnabled ? 'bg-primary-500/40' : 'hover:bg-primary-500/30'}`}
                            title={isSnapEnabled ? 'スナップOFF' : 'スナップON'}
                        >
                            <span>🧲</span>
                            <span className="text-xs">{isSnapEnabled ? 'スナップON' : 'スナップOFF'}</span>
                        </button>
                        {/* UIトグルボタン */}
                        <button
                            onClick={() => setIsUiHidden(true)}
                            className="px-4 py-2 glass-effect hover:bg-primary-500/30 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                            title="UIを非表示"
                        >
                            <span>👁️‍🗨️</span>
                            <span className="text-xs">UI非表示</span>
                        </button>
                    </div>
                </div>
            )}

            {/* UI表示ボタン（非表示時のみ） */}
            {isUiHidden && (
                <div className="absolute top-6 left-1/2 -translate-x-1/2 z-10">
                    <button
                        onClick={() => setIsUiHidden(false)}
                        className="px-4 py-2 glass-effect hover:bg-primary-500/30 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                        title="UIを表示"
                    >
                        <span>👁️</span>
                        <span className="text-xs">UI表示</span>
                    </button>
                </div>
            )}

            {/* 左サイドバー - 干渉チェック + 家具追加 */}
            {!isUiHidden && (
                <div className="absolute top-6 left-6 z-10 w-80 space-y-4">
                    <ResultPanel
                        furnitureList={furnitureList}
                        collisionResult={collisionResult}
                        ignoredCollisions={ignoredCollisions}
                    />
                    <InputForm onSizeChange={handleAddFurniture} />
                </div>
            )}

            {/* 右サイドバー - 配置済み家具 */}
            {!isUiHidden && (
                <div className="absolute top-6 right-6 z-10 w-72">
                    <FurnitureList
                        furnitureList={furnitureList}
                        collisionResult={collisionResult}
                        onDelete={handleDeleteFurniture}
                        onSelect={handleSelectFurniture}
                        selectedId={selectedId}
                        onUpdateSize={handleUpdateFurnitureSize}
                        onUpdatePosition={handleUpdateFurniturePosition}
                        onUpdateSubtype={handleUpdateFurnitureSubtype}
                        onUpdateRotation={handleUpdateFurnitureRotation}
                        room={room}
                        ignoredCollisions={ignoredCollisions}
                        onIgnoreCollision={handleIgnoreCollision}
                        onUnignoreCollision={handleUnignoreCollision}
                    />
                </div>
            )}

            {/* 3Dキャンバス */}
            <div ref={canvasContainerRef} className="w-full h-full relative">
                {/* ガイドオーバーレイ */}
                {showGuides && (
                    <div className="absolute inset-0 pointer-events-none z-10">
                        {/* 外枠 */}
                        <div className="absolute inset-4 border-2 border-yellow-400/30 rounded-lg" />
                        {/* 中央十字線 */}
                        <div className="absolute top-1/2 left-0 w-full h-px bg-yellow-400/30" />
                        <div className="absolute left-1/2 top-0 w-px h-full bg-yellow-400/30" />
                        {/* 中央ドット */}
                        <div className="absolute top-1/2 left-1/2 w-1.5 h-1.5 bg-yellow-400 rounded-full transform -translate-x-1/2 -translate-y-1/2" />
                    </div>
                )}
                <Canvas
                    camera={canvasCamera}
                    className="w-full h-full"
                    gl={canvasGl}
                    dpr={canvasDpr}
                >
                    <CameraUpdater position={cameraPosition} controlsRef={controlsRef} />
                    <Room3D
                        room={room}
                        furnitureList={furnitureList}
                        collisionResult={collisionResult}
                        selectedId={selectedId}
                        ignoredCollisions={ignoredCollisions}
                        sceneRef={sceneRef}
                        furnitureGroupRef={furnitureGroupRef}
                        onSelect={handleSelectFurniture}
                        controlsRef={controlsRef}
                        onUpdatePosition={handleUpdateFurniturePosition}
                        onUpdatePositionBatch={handleUpdateFurniturePositionBatch}
                        onUpdateSize={handleUpdateFurnitureSize}
                        onUpdateSizeAndPosition={handleUpdateFurnitureSizeAndPosition}
                        onUpdateRotation={handleUpdateFurnitureRotation}
                        onUpdateSubtype={handleUpdateFurnitureSubtype}
                        onDelete={handleDeleteFurniture}
                        onDuplicate={handleDuplicateFurniture}
                        onIgnoreCollision={handleIgnoreCollision}
                        onUnignoreCollision={handleUnignoreCollision}
                        isSnapEnabled={isSnapEnabled}
                        openContextMenuId={openContextMenuId}
                        onContextMenuChange={setOpenContextMenuId}
                        isMobile={isMobile}
                    />
                    <OrbitControls
                        ref={controlsRef}
                        enableDamping
                        dampingFactor={0.05}
                        minDistance={5}
                        maxDistance={25}
                        target={[0, 1.2, 0]}
                        enablePan
                    />
                </Canvas>
            </div>


            {/* 左下 - タイトル */}
            {!isUiHidden && (
                <div className="absolute bottom-6 left-6 z-10 pointer-events-none">
                    <div className="glass-effect rounded-xl px-5 py-3 w-80">
                        <img src="/MainLogoMini.png" alt="Furniture Checker" className="w-full h-auto" />
                    </div>
                </div>
            )}

            {/* フッター操作説明 */}
            {!isUiHidden && (
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="glass-effect rounded-xl px-6 py-2 inline-block">
                        <p className="text-xs text-gray-400">
                            【家具】左クリック: 選択・移動 右クリック: 編集メニュー | 【背景】ドラッグ: 回転 右ドラッグ: 移動 ホイール: ズーム
                        </p>
                    </div>
                </div>
            )}

            {/* 右下ボタングループ */}
            {!isUiHidden && (
                <div className="absolute bottom-6 right-6 z-20 flex gap-3">
                    {/* 部屋設定ボタン */}
                    <button
                        onClick={() => {
                            setIsFirstTimeSetup(false)
                            setIsRoomSettingsModalOpen(true)
                        }}
                        className="px-4 py-3 glass-effect hover:bg-primary-500/30 text-white font-bold rounded-2xl shadow-lg transition-all duration-200 hover:scale-105 flex items-center gap-2"
                        title="部屋設定"
                    >
                        <span className="text-xl">⚙️</span>
                        <span>部屋設定</span>
                    </button>
                    {/* 完成ボタン */}
                    <button
                        onClick={() => setIsResultModalOpen(true)}
                        className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-2xl shadow-lg transition-all duration-200 hover:scale-105 flex items-center gap-2"
                    >
                        <span className="text-xl">✨</span>
                        <span>完成！</span>
                    </button>
                </div>
            )}

            {/* リザルトモーダル */}
            <ResultModal
                isOpen={isResultModalOpen}
                onClose={() => setIsResultModalOpen(false)}
                furnitureList={furnitureList}
                canvasRef={canvasContainerRef}
                sceneRef={sceneRef}
                furnitureGroupRef={furnitureGroupRef}
            />

            {/* 部屋設定モーダル */}
            <RoomSettingsModal
                isOpen={isRoomSettingsModalOpen}
                onClose={() => {
                    setIsRoomSettingsModalOpen(false)
                    setIsFirstTimeSetup(false)
                }}
                room={room}
                onRoomChange={setRoom}
                isFirstTime={isFirstTimeSetup}
            />
        </div>
    )
}

export default App
